import React, { FC, Fragment, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { emailRegExp } from 'config/regex';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { useQuery } from '@apollo/client';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { updateLabNotificationsTemplate } from 'apollo/templates/organization';
import { useMutation } from '@apollo/client';
import {
    Box,
    Fab,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { User } from 'models/user';
import ExternalTechnicianNotificationSettingTable from './external-technician-notification-setting-table';
import LabNotificationSettingTable from './lab-notification-setting-table';
import PlacedOrderNotificationSettingTable from './placed-order-notification-setting-table';
import SystemNotificationSettingTable from './system-notification-setting-table';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import LocalDentistNotificationSettingTable from './local-dentist-notification-setting-table';

const LabNotificationSettings: FC = () => {
    const currentLab = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [usersEmail, setUsersEmail] = useState<string[]>([]);
    const [isEmailFieldValid, setIsEmailFieldValid] = useState(true);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const [generalNotifications, setGeneralNotifications] = useState({
        newOrder: false,
        newMessage: false,
        orderModified: false,
        orderResend: false,
        orderCancelled: false,
        orderRated: false,
        // DD-3492: orders this lab placed with a partner lab. "Accepted" defaults off.
        orderReceivedByLab: false,
        orderSentByLab: true,
        orderDeclined: true,
        placedOrderNewMessage: true,
        // DD-3633: "System notifications".
        priceRequests: true,
        invoiceBatches: true,
    });

    const [emailNotifications, setEmailNotifications] = useState({
        type: 'user',
        newOrder: false,
        newMessage: false,
        orderModified: false,
        orderResend: false,
        orderCancelled: false,
        orderRated: false,
        orderReceivedByLab: false,
        orderSentByLab: false,
        orderDeclined: false,
        placedOrderNewMessage: false,
        // DD-3633: price-request emails were always sent before the switch existed.
        priceRequests: true,
        invoiceBatches: false,
        otherEmail: [] as string[],
    });

    const [externalGeneralNotifications, setExternalGeneralNotifications] = useState({
        newInternalMessage: false,
        internalOrderModified: false,
        orderModified: false,
        orderDeclined: false,
        assignExtTech: false,
    });

    const [externalEmailNotifications, setExternalEmailNotifications] = useState({
        newInternalMessage: false,
        internalOrderModified: false,
        orderModified: false,
        orderDeclined: false,
        assignExtTech: false,
    });

    const [localDentistEmailNotifications, setLocalDentistEmailNotifications] = useState({
        orderRecievedByLab: false,
        orderSentByLab: false,
    });

    const [notificationListDefaults, setNotificationListDefaults] = useState({
        showOnlyUnread: true,
        showNotDone: true,
    });

    const { loading } = useQuery(labSettingsTemplate, {
        // Settings must reflect what is saved, not a cached copy — the update
        // mutation returns LabNotifications, which never patches the cached
        // Organization, so a cache-first read here can render stale values
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }) {
            setGeneralNotifications({
                ...generalNotifications,
                newOrder: currentLab.generalNotifications.newOrder,
                newMessage: currentLab.generalNotifications.newMessage,
                orderModified: currentLab.generalNotifications.orderModified,
                orderResend: currentLab.generalNotifications.orderResend,
                orderCancelled: currentLab.generalNotifications.orderCancelled,
                orderRated: currentLab.generalNotifications.orderRated,
                orderReceivedByLab: currentLab.generalNotifications.orderReceivedByLab ?? false,
                orderSentByLab: currentLab.generalNotifications.orderSentByLab ?? true,
                orderDeclined: currentLab.generalNotifications.orderDeclined ?? true,
                placedOrderNewMessage: currentLab.generalNotifications.placedOrderNewMessage ?? true,
                priceRequests: currentLab.generalNotifications.priceRequests ?? true,
                invoiceBatches: currentLab.generalNotifications.invoiceBatches ?? true,
            });
            setEmailNotifications({
                ...emailNotifications,
                newOrder: currentLab.emailNotifications.newOrder,
                newMessage: currentLab.emailNotifications.newMessage,
                orderModified: currentLab.emailNotifications.orderModified,
                orderResend: currentLab.emailNotifications.orderResend,
                orderCancelled: currentLab.emailNotifications.orderCancelled,
                orderRated: currentLab.emailNotifications.orderRated,
                orderReceivedByLab: currentLab.emailNotifications.orderReceivedByLab ?? false,
                orderSentByLab: currentLab.emailNotifications.orderSentByLab ?? false,
                orderDeclined: currentLab.emailNotifications.orderDeclined ?? false,
                placedOrderNewMessage: currentLab.emailNotifications.placedOrderNewMessage ?? false,
                priceRequests: currentLab.emailNotifications.priceRequests ?? true,
                invoiceBatches: currentLab.emailNotifications.invoiceBatches ?? false,
                otherEmail:
                    currentLab.emailNotifications?.otherEmail?.length > 0
                        ? currentLab.emailNotifications.otherEmail.split(',')
                        : [],
                type: currentLab.emailNotifications.type,
            });
            if (currentLab.externalGeneralNotifications) {
                setExternalGeneralNotifications({
                    ...externalGeneralNotifications,
                    newInternalMessage: currentLab.externalGeneralNotifications.newInternalMessage,
                    internalOrderModified: currentLab.externalGeneralNotifications.internalOrderModified,
                    orderModified: currentLab.externalGeneralNotifications.orderModified,
                    orderDeclined: currentLab.externalGeneralNotifications.orderDeclined,
                    assignExtTech: currentLab.externalGeneralNotifications.assignExtTech,
                });
            }
            if (currentLab.externalEmailNotifications) {
                setExternalEmailNotifications({
                    ...externalEmailNotifications,
                    newInternalMessage: currentLab.externalEmailNotifications.newInternalMessage,
                    internalOrderModified: currentLab.externalEmailNotifications.internalOrderModified,
                    orderModified: currentLab.externalEmailNotifications.orderModified,
                    orderDeclined: currentLab.externalEmailNotifications.orderDeclined,
                    assignExtTech: currentLab.externalEmailNotifications.assignExtTech,
                });
            }
            if (currentLab.localDentistEmailNotifications) {
                setLocalDentistEmailNotifications({
                    ...localDentistEmailNotifications,
                    orderRecievedByLab: currentLab.localDentistEmailNotifications.orderRecievedByLab,
                    orderSentByLab: currentLab.localDentistEmailNotifications.orderSentByLab,
                });
            }
            if (currentLab.notificationListDefaults) {
                setNotificationListDefaults({
                    showOnlyUnread: currentLab.notificationListDefaults.showOnlyUnread ?? true,
                    showNotDone: currentLab.notificationListDefaults.showNotDone ?? true,
                });
            }
        },
    });

    const [updateLabNotifications, { error: updateError }] = useMutation(updateLabNotificationsTemplate, {
        onCompleted: (data) => {
            // Keep the app-wide organization state in sync so the notifications
            // page picks up the new default filters without a reload
            currentOrgVar({
                ...currentOrgVar(),
                notificationListDefaults: data.updateLabNotifications.notificationListDefaults,
            });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
    });

    const handleGeneralNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGeneralNotifications({
            ...generalNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleEmailNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailNotifications({
            ...emailNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleExternalGeneralNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalGeneralNotifications({
            ...externalGeneralNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleExternalEmailNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalEmailNotifications({
            ...externalEmailNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleLocalDentistEmailNotifications = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDentistEmailNotifications({
            ...localDentistEmailNotifications,
            [event.target.name]: event.target.checked,
        });
    };

    const handleRadioType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailNotifications({
            ...emailNotifications,
            type: event.target.value,
        });
    };
    const handleFieldChange = (name: string, value: string[]) => {
        setEmailNotifications({
            ...emailNotifications,
            [name]: value,
        });
    };

    const validateEmail = (value: string) => {
        return emailRegExp.test(String(value).toLowerCase());
    };

    const handleToError = (err: Error | null) => {
        setIsEmailFieldValid(!err);
    };

    const { error } = useQuery(getTechniciansForUserListTemplate, {
        variables: {
            labId: currentLab?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setUsersEmail(technicians.map((user: User) => user.email));
        },
    });

    const handleSaveChanges = async () => {
        const notifications = JSON.parse(
            JSON.stringify({
                emailNotifications: emailNotifications,
                generalNotifications: generalNotifications,
                externalGeneralNotifications: externalGeneralNotifications,
                externalEmailNotifications: externalEmailNotifications,
                localDentistEmailNotifications: localDentistEmailNotifications,
                notificationListDefaults: notificationListDefaults,
            }),
        );
        notifications.emailNotifications.otherEmail = notifications.emailNotifications.otherEmail.toString() as any;
        await updateLabNotifications({
            variables: { labNotificationsSettings: notifications },
        });
    };

    const disableSave = () => {
        if (emailNotifications.type === 'other' && !isEmailFieldValid) {
            return true;
        }
        return false;
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {updateError ? <AlertSnackbar type="error" message={updateError?.message} /> : undefined}
            <PageTitle headingStyle="h2" title={translate('lab-settings:NOTIFICATIONS')} />
            {!loading ? (
                <Fragment>
                    <LabNotificationSettingTable
                        generalNotifications={generalNotifications}
                        emailNotifications={emailNotifications}
                        handleGeneralNotifications={handleGeneralNotifications}
                        handleEmailNotifications={handleEmailNotifications}
                    />

                    <PlacedOrderNotificationSettingTable
                        generalNotifications={generalNotifications}
                        emailNotifications={emailNotifications}
                        handleGeneralNotifications={handleGeneralNotifications}
                        handleEmailNotifications={handleEmailNotifications}
                    />

                    <SystemNotificationSettingTable
                        generalNotifications={generalNotifications}
                        emailNotifications={emailNotifications}
                        handleGeneralNotifications={handleGeneralNotifications}
                        handleEmailNotifications={handleEmailNotifications}
                    />

                    <Box mb={4} display="grid" gridTemplateRows="repeat(auto-fill, 10fr)">
                        <Typography variant="h3">{translate('lab-settings:EMAIL_FOR_NOTIFICATIONS')}</Typography>
                        <FormControl>
                            <RadioGroup
                                value={emailNotifications.type}
                                onChange={handleRadioType}
                                style={{ marginTop: '1rem' }}
                            >
                                <FormControlLabel value="user" control={<Radio />} label={usersEmail?.join(', ')} />

                                <FormControlLabel
                                    style={{ marginTop: '1rem' }}
                                    value="other"
                                    control={<Radio />}
                                    label={
                                        <ChipInput
                                            required={emailNotifications.type === 'other'}
                                            label={translate('lab-settings:EMAIL_ADDRESSES')}
                                            placeholder={translate('lab-settings:ENTER_EMAIL_ADDRESS')}
                                            options={[]}
                                            createChipOnBlur
                                            chipValidator={validateEmail}
                                            onError={handleToError}
                                            defaultValue={emailNotifications.otherEmail || []}
                                            onValueChange={(value: string[]) => handleFieldChange('otherEmail', value)}
                                            onDelete={(value: string[]) => handleFieldChange('otherEmail', value)}
                                        />
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    <ExternalTechnicianNotificationSettingTable
                        externalGeneralNotifications={externalGeneralNotifications}
                        externalEmailNotifications={externalEmailNotifications}
                        handleExternalGeneralNotifications={handleExternalGeneralNotifications}
                        handleExternalEmailNotifications={handleExternalEmailNotifications}
                    />

                    <LocalDentistNotificationSettingTable
                        localDentistEmailNotifications={localDentistEmailNotifications}
                        handleLocalDentistEmailNotifications={handleLocalDentistEmailNotifications}
                    />

                    <Box mb={4}>
                        <Typography variant="h3">
                            {translate('lab-settings:NOTIFICATION_LIST_DEFAULT_FILTERS')}
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="60%">{translate('lab-settings:FILTER')}</TableCell>
                                        <TableCell>{translate('common:DEFAULT')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>{translate('lab-settings:DEFAULT_SHOW_ONLY_UNREAD')}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={notificationListDefaults.showOnlyUnread}
                                                onChange={(_, checked) =>
                                                    setNotificationListDefaults({
                                                        ...notificationListDefaults,
                                                        showOnlyUnread: checked,
                                                    })
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>{translate('lab-settings:DEFAULT_DONE_FILTER')}</TableCell>
                                        <TableCell>
                                            <Select
                                                size="small"
                                                sx={{ minWidth: 180 }}
                                                value={notificationListDefaults.showNotDone ? 'notDone' : 'all'}
                                                onChange={(event) =>
                                                    setNotificationListDefaults({
                                                        ...notificationListDefaults,
                                                        showNotDone: event.target.value === 'notDone',
                                                    })
                                                }
                                            >
                                                <MenuItem value="notDone">
                                                    {translate('lab-settings:DEFAULT_SHOW_NOT_DONE')}
                                                </MenuItem>
                                                <MenuItem value="all">
                                                    {translate('lab-settings:DEFAULT_SHOW_ALL')}
                                                </MenuItem>
                                            </Select>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Fab
                        sx={{ position: 'fixed', left: '50%', bottom: 20, zIndex: 100 }}
                        variant="extended"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={disableSave()}
                    >
                        <SaveOutlinedIcon sx={{ marginRight: 1 }} />
                        {translate('lab-settings:SAVE_CHANGES')}
                    </Fab>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default LabNotificationSettings;
